// ============================================
// PROTOCOLS
// ============================================

import { supabase } from '../../lib/supabase';
import type { Protocol, ProtocolComponent, PhaseExplanation, InventoryItem, VialReconstitution } from '../types';
import { applyPhaseAdvance, getCurrentPhase, type PhaseAdvanceAction } from '../../utils/protocol';
import { unitsForDose } from '../../utils/calculations';
import { requireUserId, throwWithContext, notify } from './_shared';
import { fetchDoseLogsForComponent, updateDoseLog } from './doseLogs';
import { addInventoryItem } from './inventory';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Guard against malformed rows reaching the UI. A protocol with no components
 * or a first component with no phases is a Protocol v1 relic (pre-migration)
 * or a partial save that didn't complete - rendering either crashes HomePage
 * on `component.phases[...]`. Drop them at the data layer and log a warning
 * so support queries can still surface them via `fetchProtocolById`.
 */
function isRenderableProtocol(protocol: Protocol): boolean {
  if (!protocol.components || protocol.components.length === 0) {
    console.warn('[cloud] dropping protocol with empty components:', protocol.id, protocol.name);
    return false;
  }
  const first = protocol.components[0];
  if (!first.phases || first.phases.length === 0) {
    console.warn('[cloud] dropping protocol with empty first-component phases:', protocol.id);
    return false;
  }
  return true;
}

export async function fetchProtocols(): Promise<Protocol[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('protocols')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throwWithContext("Couldn't load your protocols", error);
  return (data || []).map(mapProtocolFromDb).filter(isRenderableProtocol);
}

export async function fetchActiveProtocols(): Promise<Protocol[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('protocols')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('created_at', { ascending: false });
  if (error) throwWithContext("Couldn't load your active protocols", error);
  return (data || []).map(mapProtocolFromDb).filter(isRenderableProtocol);
}

export async function fetchProtocolById(id: string): Promise<Protocol | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('protocols')
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .single();
  if (error || !data) return null;
  return mapProtocolFromDb(data);
}

// ============================================
// AI summary pipeline (Step 7)
// ============================================

export interface SummarizeResponse {
  status: 'success' | 'error' | 'rate_limited';
  data?: {
    plainEnglishSummary: string;
    phaseExplanations: PhaseExplanation[];
    summarySnippet: string;
    model: string;
    generatedAt: string;
    inputTokens: number;
    outputTokens: number;
  };
  error?: string;
  resetsAt?: string;
}

// Hard cap on the sync summarize call. Haiku normally returns in 3-8s; past
// 20s something is wrong and we'd rather save the protocol without a summary
// than leave the user staring at a "Writing your plan..." button forever.
const SUMMARIZE_TIMEOUT_MS = 20_000;

// Day index -> name. Mirrors DAY_LABELS in types.ts but uses full names
// since the AI summary is conversational. The pre-formatted strings below
// get attached to each component so the model quotes them verbatim
// instead of doing index-to-name lookups (Haiku consistently slips on
// those, e.g. emitting "Saturdays" when given selectedDays=[4]).
const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

function formatTime12h(hhmm: string): string {
  // Accepts "HH:MM" 24-hour, returns "H:MM AM/PM" with no leading zero on
  // the hour and AM/PM in caps. Bails on malformed input rather than
  // throwing, since the AI prompt would still get the raw timesOfDay.
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h24 = Number(m[1]);
  const mm = m[2];
  if (h24 < 0 || h24 > 23) return hhmm;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${period}`;
}

function formatDaysList(indices: number[] | undefined): string {
  if (!indices || indices.length === 0) return '';
  const names = indices
    .filter((i) => i >= 0 && i < 7)
    .map((i) => DAY_NAMES_FULL[i]);
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function formatTimesList(times: string[] | undefined): string {
  if (!times || times.length === 0) return '';
  const formatted = times.map(formatTime12h).filter(Boolean);
  if (formatted.length === 0) return '';
  if (formatted.length === 1) return formatted[0];
  if (formatted.length === 2) return `${formatted[0]} and ${formatted[1]}`;
  return `${formatted.slice(0, -1).join(', ')}, and ${formatted[formatted.length - 1]}`;
}

/**
 * Authoritative start-timing text for the AI summary. The model must never
 * do its own date math (same contract as _selectedDaysText):
 *   scheduled          -> "You start on Thursday, July 17."
 *   paused, never ran  -> "saved but not started yet"
 *   active, starts today -> "You start today."
 *   active, already running -> NO field (a regen must not say "starting").
 */
function _startTextFor(
  protocol: Protocol,
  regenerationReason?: string,
): { _startText?: string } {
  const today = localDayStr();
  if (protocol.status === 'scheduled' && protocol.startDate) {
    if (protocol.startDate <= today) return { _startText: 'You start today.' };
    const [y, m, d] = protocol.startDate.split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });
    return { _startText: `You start on ${label}.` };
  }
  // "Not started yet" is only provably true at the initial save (the
  // dateless Start-later pause). A protocol paused AFTER running weeks
  // regenerates with the same status - it must not claim a fresh start.
  if (protocol.status === 'paused' && regenerationReason === 'initial_save') {
    return { _startText: 'This plan is saved but not started yet. You choose when to begin.' };
  }
  // Only provably "starting" at the initial save (start-today protocols).
  // A regen ON the activation day (engine or Start now) must not bake
  // "You start today." into prose that reads stale tomorrow.
  if (
    protocol.status === 'active'
    && protocol.startDate >= today
    && regenerationReason === 'initial_save'
  ) {
    return { _startText: 'You start today.' };
  }
  return {};
}

export async function callSummarizeFunction(
  protocol: Protocol,
  opts?: {
    contentLevel?: 'beginner' | 'intermediate' | 'advanced';
    reflectionContext?: string;
    regenerationReason?: 'initial_save' | 'protocol_edited' | 'manual_regen' | 'phase_advanced';
    isCombinedVial?: boolean;
    // A short, plain-English note of what just changed since the last
    // version (draw, dose, schedule). The edge function opens the summary
    // with one friendly, reassuring sentence about it. Omit on first save.
    changeNote?: string;
  },
): Promise<SummarizeResponse> {
  const { data } = await supabase.auth.getSession();
  if (!data?.session) return { status: 'error', error: 'Not authenticated' };

  const controller = new AbortController();
  const timeoutHandle = setTimeout(() => controller.abort(), SUMMARIZE_TIMEOUT_MS);

  try {
    // Both `apikey` and `Authorization` are required. Supabase's edge runtime
    // uses the apikey header to locate the project, and Authorization for
    // JWT verification. Missing apikey returns 401 even with a valid JWT.
    // (analyzeVideo has always sent both - this was the bug.)
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/summarize-protocol`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          Authorization: `Bearer ${data.session.access_token}`,
        },
        body: JSON.stringify({
          protocol: {
            ...protocol,
            // Authoritative start-timing sentence for the AI (scheduled
            // protocols, 2026-07-16). Precomputed like _selectedDaysText so
            // the model never does its own date math. Absent for a protocol
            // that is already running - a mid-life regen must not claim the
            // user is "starting".
            ..._startTextFor(protocol, opts?.regenerationReason),
            // _isCombinedVial: caller explicitly flags combined-vial protocols
            // (one shared mixture, ONE draw pulls all peptides simultaneously).
            // Fallback: v3 components sharing a mixGroupId, or (legacy) all
            // components sharing an inventoryId. The mixGroupId check matters
            // for UNTRACKED mixes - they have no inventoryId at all. Edge
            // function uses this to frame the injection as a single shared
            // draw instead of per-peptide draws.
            _isCombinedVial: opts?.isCombinedVial
              ?? ((protocol.components.length > 1
                  && protocol.components[0].mixGroupId != null
                  && protocol.components.every(
                    (c) => c.mixGroupId === protocol.components[0].mixGroupId,
                  ))
                || (protocol.components.length > 1
                  && protocol.components[0].inventoryId != null
                  && protocol.components.every(
                    (c) => c.inventoryId === protocol.components[0].inventoryId,
                  ))),
            // Pre-calculate authoritative human-readable strings for the AI:
            //   - _unitsToFill per phase: avoids ad-hoc dose-to-units math
            //   - _selectedDaysText / _timesOfDayText per component: avoids
            //     small-model day-index slips (e.g. emitting "Saturdays" when
            //     selectedDays=[4]) and time rounding (08:03 → "8 AM")
            //   - same overrides on phases that override component schedule
            components: protocol.components.map((comp) => ({
              ...comp,
              _selectedDaysText: formatDaysList(comp.selectedDays),
              _timesOfDayText: formatTimesList(comp.timesOfDay),
              phases: comp.phases.map((phase) => ({
                ...phase,
                _unitsToFill: comp.concentrationMcgPerUnit > 0
                  ? unitsForDose(phase.doseMcg, comp.concentrationMcgPerUnit)
                  : null,
                ...(phase.selectedDaysOverride
                  ? { _selectedDaysOverrideText: formatDaysList(phase.selectedDaysOverride) }
                  : {}),
                ...(phase.timesOfDayOverride
                  ? { _timesOfDayOverrideText: formatTimesList(phase.timesOfDayOverride) }
                  : {}),
              })),
            })),
          },
          contentLevel: opts?.contentLevel ?? 'beginner',
          reflectionContext: opts?.reflectionContext,
          regenerationReason: opts?.regenerationReason ?? 'initial_save',
          changeNote: opts?.changeNote,
        }),
        signal: controller.signal,
      },
    );

    if (!res.ok && res.status !== 429) {
      return { status: 'error', error: `HTTP ${res.status}` };
    }
    return (await res.json()) as SummarizeResponse;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { status: 'error', error: 'timeout' };
    }
    const msg = err instanceof Error ? err.message : String(err);
    return { status: 'error', error: msg };
  } finally {
    clearTimeout(timeoutHandle);
  }
}

// Fields whose change means the plain-English summary is probably stale and
// should be regenerated in the background. Cosmetic changes (isActive, notes,
// startDate, userEditedSummary itself) are intentionally excluded.
const MATERIAL_SUMMARY_FIELDS: (keyof Protocol)[] = ['components', 'name'];

/**
 * Plain-English note of what changed between two protocol versions, for the
 * AI summary's opening reassurance line. Category-level on purpose (no mcg/mg,
 * no math) - the model already has the new numbers and converts to units.
 * Returns null when nothing worth calling out changed (e.g. a rename only),
 * so the edge function knows to skip the opener.
 */
function describeProtocolChanges(before: Protocol, next: Protocol): string | null {
  const beforeById = new Map(before.components.map((c) => [c.id, c]));
  let doseUp = false;
  let doseDown = false;
  let daysChanged = false;
  let timesChanged = false;
  for (const nc of next.components) {
    const bc = beforeById.get(nc.id);
    if (!bc) continue;
    const bDose = bc.phases.reduce((s, p) => s + p.doseMcg, 0);
    const nDose = nc.phases.reduce((s, p) => s + p.doseMcg, 0);
    if (nDose > bDose) doseUp = true;
    else if (nDose < bDose) doseDown = true;
    if (
      bc.frequency !== nc.frequency ||
      JSON.stringify(bc.selectedDays ?? []) !== JSON.stringify(nc.selectedDays ?? [])
    ) {
      daysChanged = true;
    }
    if (JSON.stringify(bc.timesOfDay ?? []) !== JSON.stringify(nc.timesOfDay ?? [])) {
      timesChanged = true;
    }
  }
  const notes: string[] = [];
  if (doseUp && !doseDown) notes.push('The dose went up.');
  else if (doseDown && !doseUp) notes.push('The dose went down.');
  else if (doseUp && doseDown) notes.push('The dose changed.');
  if (daysChanged) notes.push('Which days they take it changed.');
  if (timesChanged) notes.push('The time of day changed.');
  return notes.length > 0 ? notes.join(' ') : null;
}

/**
 * Generate the initial AI summary and stamp it onto `protocol` in place.
 * Best-effort: on any failure (network, rate limit, parse error) the
 * protocol saves without a summary and the background path in
 * updateProtocol can backfill later. Skips generation when the caller
 * already pre-generated one (Phase 2D: the review screen streams it in
 * parallel so the user sees it before tapping Save). Shared by every
 * first-save path (addProtocol + the atomic reconstitute save).
 */
async function generateInitialSummary(protocol: Protocol): Promise<void> {
  const hasPreGeneratedSummary = !!protocol.plainEnglishSummary
    && !!protocol.summaryGeneratedAt;
  if (hasPreGeneratedSummary) return;
  try {
    const summaryResponse = await callSummarizeFunction(protocol, {
      regenerationReason: 'initial_save',
    });
    if (summaryResponse.status === 'success' && summaryResponse.data) {
      protocol.plainEnglishSummary = summaryResponse.data.plainEnglishSummary;
      protocol.phaseExplanations = summaryResponse.data.phaseExplanations;
      protocol.summaryGeneratedAt = summaryResponse.data.generatedAt;
      protocol.summaryModel = summaryResponse.data.model;
    } else if (summaryResponse.status === 'rate_limited') {
      console.warn('[cloud] summary rate-limited, saving without summary');
    } else if (summaryResponse.status === 'error') {
      console.warn('[cloud] summary failed, saving without summary:', summaryResponse.error);
    }
  } catch (err) {
    console.warn('[cloud] summary generation threw, saving without summary:', err);
  }
}

/**
 * Flip the onboarding flag once the first protocol lands. Written
 * unconditionally: an idempotent UPDATE on an already-TRUE row is a
 * no-op. has_created_first_protocol is NOT in the protected-columns list
 * of guard_user_profiles_protected_columns, so the standard RLS policy
 * lets the user update their own row directly without an edge function.
 * Errors here are non-fatal (the protocol saved; the empty-dashboard
 * logic is cosmetic).
 */
async function markFirstProtocolFlag(userId: string): Promise<void> {
  try {
    await supabase
      .from('user_profiles')
      .update({
        has_created_first_protocol: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);
    notify('user_profiles');
  } catch (err) {
    console.warn('[cloud] could not flip has_created_first_protocol flag:', err);
  }
}

/**
 * Write the new inventory rows AFTER a confirmed protocol save (C4). If a
 * vial write fails the protocol stands and tracking degrades gracefully:
 * the failed vial's inventoryId references are cleared from the saved
 * components (best-effort) so the end-state matches Skip at the track-vial
 * step. Returns the vials that failed so the caller can toast. Shared by
 * saveProtocolWithVials + the atomic reconstitute save.
 */
async function insertVialsBestEffort(
  protocol: Protocol,
  vials: InventoryItem[],
): Promise<InventoryItem[]> {
  const failedVials: InventoryItem[] = [];
  for (const vial of vials) {
    try {
      await addInventoryItem(vial);
    } catch (err) {
      console.error('[cloud] vial write failed after protocol save, degrading to untracked:', err);
      failedVials.push(vial);
    }
  }

  // Best-effort: clear dangling inventoryId references for failed vials.
  // Direct components update (not updateProtocol) so this doesn't count as
  // a material change and re-trigger AI summary generation. Failure here is
  // non-fatal - lookups by a missing inventoryId already render as untracked.
  if (failedVials.length > 0) {
    try {
      const userId = await requireUserId();
      const failedIds = new Set(failedVials.map((v) => v.id));
      const cleaned = protocol.components.map((c) =>
        c.inventoryId && failedIds.has(c.inventoryId)
          ? { ...c, inventoryId: undefined }
          : c,
      );
      await supabase
        .from('protocols')
        .update({ components: cleaned })
        .eq('id', protocol.id)
        .eq('user_id', userId);
      notify('protocols');
    } catch (err) {
      console.warn('[cloud] could not clear dangling inventoryIds:', err);
    }
  }

  return failedVials;
}

export async function addProtocol(protocol: Protocol): Promise<void> {
  const userId = await requireUserId();
  await generateInitialSummary(protocol);

  const row = mapProtocolToDb(protocol, userId);
  const { error } = await supabase.from('protocols').insert(row);
  if (error) throwWithContext("Couldn't create your protocol", error);
  notify('protocols');

  await markFirstProtocolFlag(userId);
}

/**
 * Add Protocol v3 save strategy (C4, locked by CEO review): the
 * protocol + its components are ONE row insert (atomic for free);
 * vial rows write AFTER the protocol confirms. If a vial write fails,
 * the protocol stands and tracking degrades gracefully - the failed
 * vial's inventoryId references are cleared from the saved components
 * (best-effort) so the end-state is identical to choosing Skip at the
 * track-vial step. No compensating delete, no protocol-write RPC.
 *
 * Returns the vials that failed so the caller can toast ("We saved
 * your protocol but couldn't set up vial tracking - turn it on from
 * the vial tab"). The celebration screen gates on the confirmed
 * protocol write only - this function throwing means the protocol
 * did NOT save; vial failures never throw.
 */
export async function saveProtocolWithVials(
  protocol: Protocol,
  vials: InventoryItem[],
): Promise<{ failedVials: InventoryItem[] }> {
  await addProtocol(protocol);
  return { failedVials: await insertVialsBestEffort(protocol, vials) };
}

/**
 * Add Protocol v3 save for a protocol that REUSES one or more stored combo
 * vials and starts now (storage-mix wizard reuse, D4). The protocol insert
 * and the reused vials' reconstitution (flip is_researching=false + stamp
 * counts) commit together via the save_protocol_with_reconstituted_vial
 * RPC - no client-side multi-write that could leave a protocol pointing at
 * a still-stored vial (or a flipped vial with no protocol). The RPC
 * validates ownership + mix-identity + still-stored (10A / D5).
 *
 * All derived values arrive pre-computed from buildProtocol (C7); the RPC
 * does no dose math. Any brand-new (non-linked) tracked vials in the same
 * protocol write AFTER, best-effort, exactly like saveProtocolWithVials.
 * Throwing means the protocol did NOT save; new-vial failures never throw.
 */
export async function saveProtocolWithReconstitutions(
  protocol: Protocol,
  reconstitutions: VialReconstitution[],
  newVials: InventoryItem[],
): Promise<{ failedVials: InventoryItem[] }> {
  const userId = await requireUserId();
  await generateInitialSummary(protocol);

  // user_id is omitted: the RPC always stamps auth.uid() (an owner in the
  // payload would be ignored anyway - mirrors refillVial).
  const row = mapProtocolToDb(protocol);
  const { error } = await supabase.rpc('save_protocol_with_reconstituted_vial', {
    p_protocol: row,
    p_reconstitutions: reconstitutions.map((r) => ({
      inventory_id: r.inventoryId,
      bac_water_ml: r.bacWaterMl,
      concentration_mcg_per_unit: r.concentrationMcgPerUnit,
      total_doses: r.totalDoses,
      doses_remaining: r.dosesRemaining,
      // Present only for a sealed combo (fresh 28-day clock); the RPC leaves
      // the vial's dates untouched when these are absent.
      date_reconstituted: r.dateReconstituted ?? null,
      expiration_date: r.expirationDate ?? null,
      mix_group_id: r.mixGroupId,
      peptide_ids: r.peptideIds,
    })),
  });
  if (error) throwWithContext("Couldn't save your protocol", error);
  notify('protocols');
  notify('inventory');

  await markFirstProtocolFlag(userId);

  return { failedVials: await insertVialsBestEffort(protocol, newVials) };
}

/**
 * Direct components-only update that does NOT count as a material
 * change: no AI-summary invalidation or regeneration fires. Use for
 * mechanical component rewrites - vial linking, concentration
 * recompute after a vial swap - where the protocol's meaning is
 * unchanged and re-summarizing would be churn. Mirrors the direct
 * update pattern used for dangling-inventoryId cleanup above.
 */
export async function updateProtocolComponents(
  id: string,
  components: ProtocolComponent[],
): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('protocols')
    .update({ components, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't update your protocol", error);
  notify('protocols');
}

export async function updateProtocol(id: string, updates: Partial<Protocol>): Promise<void> {
  const userId = await requireUserId();

  // Load current state so we can detect material changes and merge before
  // mapping to DB shape (more robust than passing a partial to mapProtocolToDb).
  const before = await fetchProtocolById(id);
  if (!before) return;

  const isMaterialChange = MATERIAL_SUMMARY_FIELDS.some((f) => f in updates);
  const next: Protocol = { ...before, ...updates, updatedAt: new Date().toISOString() };

  const dbUpdates = mapProtocolToDb(next);
  delete (dbUpdates as Record<string, unknown>).user_id;
  // On material changes invalidate the summary timestamp so the UI can show
  // a "regenerating" state until the background refresh lands.
  if (isMaterialChange) {
    (dbUpdates as Record<string, unknown>).summary_generated_at = null;
  }

  const { error } = await supabase
    .from('protocols')
    .update(dbUpdates)
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't update your protocol", error);
  notify('protocols');

  // Async summary regeneration on material change - fire-and-forget. We skip
  // this when the user has an explicit override (userEditedSummary) so the
  // background pipeline doesn't silently clobber their edits.
  if (isMaterialChange && !next.userEditedSummary) {
    const changeNote = describeProtocolChanges(before, next) ?? undefined;
    callSummarizeFunction(next, { regenerationReason: 'protocol_edited', changeNote })
      .then(async (response) => {
        if (response.status === 'success' && response.data) {
          await supabase
            .from('protocols')
            .update({
              plain_english_summary: response.data.plainEnglishSummary,
              phase_explanations: response.data.phaseExplanations,
              summary_generated_at: response.data.generatedAt,
              summary_model: response.data.model,
            })
            .eq('id', id)
            .eq('user_id', userId);
          notify('protocols');
        }
      })
      .catch((err) => console.error('[cloud] background regen failed:', err));
  }
}

/**
 * Fire-and-forget summary refresh after a write that changed what the
 * stored summary SAYS but skipped the regeneration pipeline: a draw-units
 * change (refill, auto-promoted backup, re-link), a start-date change
 * ("You start on July 17" must move), or activation itself (the summary
 * must stop claiming the user is "starting"). Takes a protocol id and
 * re-fetches the freshest full protocol (so it works no matter which path
 * just wrote), nulls the timestamp (UI shows a regenerating state),
 * regenerates with a plain-English "what changed" note, and writes back.
 * Respects an explicit user-edited summary. Returns immediately; never
 * throws.
 */
export type SummaryRefreshOutcome = 'refreshed' | 'rate_limited' | 'failed' | 'skipped';

export function refreshProtocolSummary(protocolId: string, changeNote?: string): Promise<SummaryRefreshOutcome> {
  return requireUserId()
    .then(async (userId): Promise<SummaryRefreshOutcome> => {
      const protocol = await fetchProtocolById(protocolId);
      if (!protocol || protocol.userEditedSummary) return 'skipped';

      // Invalidate first so the detail page can show "updating your plan".
      const prevGeneratedAt = protocol.summaryGeneratedAt ?? null;
      await supabase
        .from('protocols')
        .update({ summary_generated_at: null })
        .eq('id', protocolId)
        .eq('user_id', userId);
      notify('protocols');

      let response: SummarizeResponse;
      try {
        response = await callSummarizeFunction(protocol, { regenerationReason: 'protocol_edited', changeNote });
      } catch (err) {
        response = { status: 'error', error: String(err) };
      }
      if (response.status === 'success' && response.data) {
        await supabase
          .from('protocols')
          .update({
            plain_english_summary: response.data.plainEnglishSummary,
            phase_explanations: response.data.phaseExplanations,
            summary_generated_at: response.data.generatedAt,
            summary_model: response.data.model,
          })
          .eq('id', protocolId)
          .eq('user_id', userId);
        notify('protocols');
        return 'refreshed';
      }
      if (prevGeneratedAt) {
        // Regen failed (rate limited, offline, model error): restore the
        // previous timestamp so the existing summary shows normally again
        // instead of sitting in a permanent "updating" state. The old text
        // was never cleared - stale beats stuck.
        await supabase
          .from('protocols')
          .update({ summary_generated_at: prevGeneratedAt })
          .eq('id', protocolId)
          .eq('user_id', userId);
        notify('protocols');
      }
      return response.status === 'rate_limited' ? 'rate_limited' : 'failed';
    })
    .catch((err) => {
      console.error('[cloud] summary refresh failed:', err);
      return 'failed' as const;
    });
}

/** Local calendar day (yyyy-mm-dd) - date-only writes must never come from
 *  a UTC formatter. */
function localDayStr(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export async function updateProtocolStatus(id: string, status: Protocol['status']): Promise<void> {
  const userId = await requireUserId();

  const updates: Record<string, unknown> = {
    status,
    is_active: status === 'active',
    updated_at: new Date().toISOString(),
  };
  if (status === 'active') {
    // Activating a protocol whose start date never arrived (scheduled ->
    // cancel schedule -> paused -> Resume) must restamp start_date to
    // today. Without it the row goes live with a FUTURE start date: no
    // doses due, nothing pre-scheduled, negative day counters - a dormant
    // "active" protocol (adversarial review B2). On-time resumes are
    // untouched (their start_date is already in the past).
    const { data: row } = await supabase
      .from('protocols')
      .select('start_date')
      .eq('id', id)
      .eq('user_id', userId)
      .single();
    if (row?.start_date && row.start_date > localDayStr()) {
      updates.start_date = localDayStr();
    }
  }

  const { error } = await supabase
    .from('protocols')
    .update(updates)
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't update protocol status", error);
  notify('protocols');
}

// ────────────────────────────────────────────────────────────
// Scheduled protocols - activation (2026-07-16)
// ────────────────────────────────────────────────────────────

/**
 * Scheduled protocols whose start date has arrived (start_date <= the
 * caller's LOCAL calendar day). Server-side filter so the reconcile
 * engine's common case - nothing due - is one cheap indexed read.
 */
export async function fetchDueScheduledProtocols(todayLocal: string): Promise<Protocol[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('protocols')
    .select('*')
    .eq('user_id', userId)
    .eq('status', 'scheduled')
    .lte('start_date', todayLocal)
    .order('created_at', { ascending: false });
  if (error) throwWithContext("Couldn't load your scheduled protocols", error);
  return (data || []).map(mapProtocolFromDb).filter(isRenderableProtocol);
}

/** One vial stamp for `activate_scheduled_protocol`. All derived values are
 *  client-computed (C7); null counts keep an approximate vial's C6 clamp. */
export interface ActivationReconstitution {
  inventoryId: string;
  bacWaterMl?: number;
  concentrationMcgPerUnit?: number;
  totalDoses: number | null;
  dosesRemaining: number | null;
  /** Only for a vial being mixed fresh - absent keeps the existing clock. */
  dateReconstituted?: string;
  expirationDate?: string;
}

/** Raised by the RPC when another device already activated (or the user
 *  paused/deleted) the protocol - callers repair reminders and move on. */
export const ACTIVATE_NOT_SCHEDULED = 'activate_protocol_not_scheduled';

/**
 * Atomic scheduled -> active flip via the activate_scheduled_protocol RPC:
 * restamps start_date to the local activation day (D-Restamp) and
 * reconstitutes still-parked vials in the same transaction. Throwing means
 * NOTHING changed (the RPC rolls back whole) - the protocol stays scheduled
 * and the reconcile engine retries on its next trigger.
 */
export async function activateScheduledProtocol(
  protocolId: string,
  startDateLocal: string,
  reconstitutions: ActivationReconstitution[],
): Promise<void> {
  const { error } = await supabase.rpc('activate_scheduled_protocol', {
    p_protocol_id: protocolId,
    p_start_date: startDateLocal,
    p_reconstitutions: reconstitutions.map((r) => ({
      inventory_id: r.inventoryId,
      bac_water_ml: r.bacWaterMl ?? null,
      concentration_mcg_per_unit: r.concentrationMcgPerUnit ?? null,
      total_doses: r.totalDoses,
      doses_remaining: r.dosesRemaining,
      date_reconstituted: r.dateReconstituted ?? null,
      expiration_date: r.expirationDate ?? null,
    })),
  });
  if (error) {
    if (error.message?.includes(ACTIVATE_NOT_SCHEDULED)) {
      const raced = new Error(ACTIVATE_NOT_SCHEDULED);
      raced.name = 'ProtocolNotScheduledError';
      throw raced;
    }
    throwWithContext("Couldn't start your protocol", error);
  }
  notify('protocols');
  notify('inventory');
}

/**
 * Change a SCHEDULED protocol's start date. Conditional on status in the
 * WHERE clause: if the activation engine flipped the protocol to active in
 * the race window (detail page open across the start moment), this becomes
 * a no-op instead of stamping a future start_date onto an ACTIVE protocol
 * (which would silence its schedule until that date - adversarial review
 * A5). Returns false when the protocol was no longer scheduled.
 */
export async function updateScheduledStartDate(id: string, startDate: string): Promise<boolean> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('protocols')
    .update({ start_date: startDate, updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', userId)
    .eq('status', 'scheduled')
    .select('id');
  if (error) throwWithContext("Couldn't change the start date", error);
  notify('protocols');
  const changed = (data?.length ?? 0) > 0;
  // The stored AI summary quotes the start date verbatim ("You start on
  // Thursday, July 17.") - a moved date must regenerate it. Restamps to
  // TODAY (the Start-now early-start path) skip this: activation follows
  // within moments and triggers its own refresh with the right framing.
  if (changed && startDate > localDayStr()) {
    const [y, m, d] = startDate.split('-').map(Number);
    const label = new Date(y, m - 1, d).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    });
    refreshProtocolSummary(id, `The start date moved to ${label}.`);
  }
  return changed;
}

export async function regenerateProtocolSummary(protocolId: string): Promise<void> {
  const userId = await requireUserId();
  const protocol = await fetchProtocolById(protocolId);
  if (!protocol) return;
  const response = await callSummarizeFunction(protocol, { regenerationReason: 'manual_regen' });
  if (response.status === 'success' && response.data) {
    await supabase
      .from('protocols')
      .update({
        plain_english_summary: response.data.plainEnglishSummary,
        user_edited_summary: null, // manual regen clears user override
        phase_explanations: response.data.phaseExplanations,
        summary_generated_at: response.data.generatedAt,
        summary_model: response.data.model,
      })
      .eq('id', protocolId)
      .eq('user_id', userId);
    notify('protocols');
  } else if (response.status === 'rate_limited') {
    throw new Error('You have used your AI summary quota for now. Try again later.');
  } else {
    throw new Error(response.error ?? 'Regeneration failed');
  }
}

export async function deleteProtocol(id: string): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('protocols')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't delete your protocol", error);
  notify('protocols');
  notify('reminders');
}

export async function countProtocols(): Promise<number> {
  const userId = await requireUserId();
  const { count, error } = await supabase
    .from('protocols')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't count your protocols", error);
  return count || 0;
}

/**
 * Apply a phase advancement decision (advance/hold/pause) and persist.
 * Optionally saves a reflection on the previous phase's most recent dose log.
 */
export async function advancePhase(
  protocolId: string,
  componentId: string,
  action: PhaseAdvanceAction,
  reflection?: string,
): Promise<void> {
  const protocol = await fetchProtocolById(protocolId);
  if (!protocol) throw new Error('Protocol not found');

  const componentIdx = protocol.components.findIndex((c) => c.id === componentId);
  if (componentIdx === -1) throw new Error('Component not found');

  const component = protocol.components[componentIdx];
  const currentPhase = getCurrentPhase(component);
  const result = applyPhaseAdvance(component, currentPhase, action);

  // Apply the component change and (if pausing) mark the protocol inactive
  const updatedComponents = [...protocol.components];
  updatedComponents[componentIdx] = result.component;
  const updates: Partial<Protocol> = {
    components: updatedComponents,
    isActive: result.protocolStillActive,
    updatedAt: new Date().toISOString(),
  };
  await updateProtocol(protocolId, updates);

  // Optional: persist reflection to the most recent dose log of the previous phase
  if (reflection) {
    const logs = await fetchDoseLogsForComponent(componentId);
    const phaseLogs = logs
      .filter((l) => l.phaseId === currentPhase.id)
      .sort((a, b) => b.loggedTime.localeCompare(a.loggedTime));
    if (phaseLogs.length > 0) {
      await updateDoseLog(phaseLogs[0].id, { phaseReflection: reflection });
    }
  }

  notify('protocols');
  notify('reminders');
  notify('dose_logs');

  // Background summary regeneration with reflection context. Only runs when
  // the user actually advanced (hold/pause don't change the story) AND they
  // left a reflection note AND there is no user-edited override to protect.
  if (action.type === 'advance_to_next' && reflection) {
    const updated = await fetchProtocolById(protocolId);
    if (updated && !updated.userEditedSummary) {
      const userId = await requireUserId();
      callSummarizeFunction(updated, {
        regenerationReason: 'phase_advanced',
        reflectionContext: reflection,
      })
        .then(async (response) => {
          if (response.status === 'success' && response.data) {
            await supabase
              .from('protocols')
              .update({
                plain_english_summary: response.data.plainEnglishSummary,
                phase_explanations: response.data.phaseExplanations,
                summary_generated_at: response.data.generatedAt,
                summary_model: response.data.model,
              })
              .eq('id', protocolId)
              .eq('user_id', userId);
            notify('protocols');
          }
        })
        .catch((err) => console.error('[cloud] phase-advance regen failed:', err));
    }
  }
}

export function mapProtocolFromDb(row: any): Protocol {
  const status: Protocol['status'] =
    row.status === 'active' || row.status === 'paused' || row.status === 'completed' || row.status === 'scheduled'
      ? row.status
      : (row.is_active ? 'active' : 'paused');
  return {
    id: row.id,
    name: row.name,
    status,
    isActive: status === 'active',
    startDate: row.start_date,
    notifyEachDose: row.notify_each_dose ?? true,
    notes: row.notes ?? undefined,
    components: Array.isArray(row.components) ? row.components : [],
    plainEnglishSummary: row.plain_english_summary ?? undefined,
    userEditedSummary: row.user_edited_summary ?? undefined,
    phaseExplanations: row.phase_explanations ?? undefined,
    summaryGeneratedAt: row.summary_generated_at ?? undefined,
    summaryModel: row.summary_model ?? undefined,
    sourceAttribution: row.source_attribution ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export function mapProtocolToDb(protocol: Protocol, userId?: string): Record<string, unknown> {
  // Writes `status` alongside `is_active` so a non-active protocol round-trips
  // correctly. Previously this OMITTED `status`, so an insert fell back to the
  // DB default 'active' even when is_active was false — the split-brain that
  // made a start-later (paused) protocol read Active in the protocols list
  // (status-filtered) while staying dormant on the dashboard (is_active-
  // filtered). Scheduled protocols depend on this too: they persist
  // status='scheduled' + is_active=false. The 20260422 migration (status
  // column) is applied in all envs and must ship ahead of this change.
  const row: Record<string, unknown> = {
    id: protocol.id,
    name: protocol.name,
    status: protocol.status,
    is_active: protocol.isActive,
    start_date: protocol.startDate,
    notify_each_dose: protocol.notifyEachDose ?? true,
    notes: protocol.notes ?? null,
    components: protocol.components,
    plain_english_summary: protocol.plainEnglishSummary ?? null,
    user_edited_summary: protocol.userEditedSummary ?? null,
    phase_explanations: protocol.phaseExplanations ?? null,
    summary_generated_at: protocol.summaryGeneratedAt ?? null,
    summary_model: protocol.summaryModel ?? null,
    source_attribution: protocol.sourceAttribution ?? null,
    created_at: protocol.createdAt,
    updated_at: protocol.updatedAt,
  };
  if (userId) row.user_id = userId;
  return row;
}
